<script setup lang="ts">

import { computed } from 'vue';
import { useNotify } from '@library/Components';
import { isDevelopment } from "@utilities/Environments";
import { requestMarketplaceCategory } from '@app/MyMarketplace/Types/useRequestMarketplaceCategory';

type APP_PAGE = 'timeline' | 'trips' | 'garage' | 'marketplace' | 'safetycenter' | 'settings' | 'vehiclesettings';
type MAP_COORDS = { lat: string, lng: string };
type SMS_DETAILS = { number?: string, message: string };
type VARIANTS = 'text' | 'outlined' | 'flat' | 'raised' | 'anchor'

interface IJupiterButtonProps
{
    type: 'page' | 'url' | 'map' | 'map-query' | 'tel' | 'email' | 'sms';
    to: MAP_COORDS | APP_PAGE | SMS_DETAILS | string;
    options?: Record<string, string>
    variant?: VARIANTS
} 

const props = withDefaults( defineProps<IJupiterButtonProps>(), { variant: 'anchor' } );
const isInWebBrowser = isDevelopment() || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test( navigator.userAgent ) == false;

let onClick = () => {};

const path = computed( () =>
{
    switch( props.type )
    {
        // Open the phone app
        case 'tel': 
            if( isInWebBrowser ) return '';
            return `carbucks://tel?number=${ props.to as string }`;

        // Open the SMS app
        case 'sms': 
            const details = props.to as SMS_DETAILS;
            const number = details.number;
            const message = details.message;
            if( isInWebBrowser ) return '';
            return `carbucks://sms?number=${number}&message=${message}`;

        // Open the default mail client
        case 'email': 
            if( isInWebBrowser ) return '';
            return `carbucks://mailto?email=${ props.to as string }`;

        // Open the default browser
        case 'url': 
            if( isInWebBrowser ) return props.to as string;
            return `carbucks://opensystembrowser?url=${ encodeURI( props.to as string ) }`;

        // Open the default map app with lat/lng
        case 'map': 
            if( isInWebBrowser ) return `https://maps.google.com.au?q=${ ( props.to as MAP_COORDS ).lat },${ ( props.to as MAP_COORDS ).lng }`;
            return `carbucks://opendefaultmap?${ new URLSearchParams( props.to as { lat: string, lng: string} ).toString() }`;

        // Open the default map app with query
        case 'map-query': 
            if( isInWebBrowser ) return `https://maps.google.com.au?query=${ encodeURI( props.to as string ) }`;
            return `carbucks://opendefaultmap?query=${ encodeURI( props.to as string ) }`;

        // Open an internal app page, or perform a routing if we're in development mode
        default: //'page'

            if( ( props.to as APP_PAGE ) == 'marketplace' )
            {
                onClick = () => {
                    requestMarketplaceCategory( props.options?.group, props.options?.categoryId );
                }
            }

            if( isInWebBrowser )
            {
                if( ( props.to as APP_PAGE ) == 'marketplace' )
                {
                    return `/my-marketplace` + ( props.options == null ? '' : `?${ new URLSearchParams( props.options ).toString() }` );
                }

                else if( ( props.to as APP_PAGE ) == 'safetycenter' )
                {
                    return `/safety` + ( props.options == null ? '' : `?${ new URLSearchParams( props.options ).toString() }` );
                }

                // Its a development navigation to a page that is in the native app
                return '';
            }

            else
            {
                return `carbucks://pushpage?pagename=${props.to}` + ( props.options == null ? '' : `&${ new URLSearchParams( props.options ).toString() }` );
            }

    }
} );

const isHref = computed( () => isInWebBrowser == false || props.type == 'url' );
const isNativeAppRequest = computed( () => isInWebBrowser && path.value == '' );
const isExternalUrl = computed( () => isInWebBrowser && props.type == 'url' );

function notify()
{
    useNotify( `Function not supported from a browser<br/>${ props.type }=${ props.to instanceof String ? props.to : '<code>' + JSON.stringify( props.to ) + '</code>' }`, '' );
}

</script>

<template>
    <a v-if="isNativeAppRequest && variant == 'anchor'" 
       @click.stop="() => { onClick(); notify() }">
        <slot></slot>
    </a>
    <Button v-else-if="isNativeAppRequest"
            :variant="variant" 
            @click.stop="() => { onClick(); notify() }">
        <slot></slot>
    </Button>
    <a v-else-if="variant == 'anchor'" 
       :href="path"
       target="_blank"
       @click.stop="() => onClick()">
        <slot></slot>
    </a>
    <Button v-else-if="isHref" 
            :variant="variant" 
            :href="path"
            target="_blank"
            @click.stop="() => onClick()">
        <slot></slot>
    </Button>
    <Button v-else 
            :variant="variant" 
            :to="path"
            @click.stop="() => onClick()">
        <slot></slot>
    </Button>
    
</template>